import axios from "axios";

/**
 * Resolve the backend base URL.
 *
 * Priority:
 *   1. If REACT_APP_BACKEND_URL is set AND points to the SAME origin as the page,
 *      use it (preserves dev/preview behaviour where both are the Emergent URL).
 *   2. Otherwise fall back to a *relative* base, so requests target whichever
 *      origin the app was loaded from. This is critical for multi-subdomain
 *      deployments (lodge.squareandcompass.store, admin.squareandcompass.store,
 *      apex squareandcompass.store) where a single bundled REACT_APP_BACKEND_URL
 *      cannot match every host the app may be served from.
 */
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === "undefined") return envUrl || "";
  if (!envUrl) return "";
  try {
    const envOrigin = new URL(envUrl).origin;
    if (envOrigin === window.location.origin) return envUrl;
    // Different origin → use relative paths so we always hit the host serving the app.
    return "";
  } catch {
    return "";
  }
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Send Accept-Language so the backend can localise error strings.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sc_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    config.headers = config.headers || {};
    config.headers["Accept-Language"] = currentLangCode();
  } catch {
    /* ignore */
  }
  // Forward the active child-lodge impersonation (set by the obedience admin
  // via the "Enter lodge" button on the Users page). The backend treats this
  // header as a temporary scope override.
  try {
    const activeChild =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("sc_active_child_lodge")
        : null;
    if (activeChild) {
      config.headers = config.headers || {};
      config.headers["X-Active-Child-Lodge"] = activeChild;
    }
  } catch {
    /* ignore */
  }
  return config;
});

/**
 * Detect the LODGE_FROZEN signal from the backend (HTTP 403 with structured detail).
 * Dispatches a global window event so the UI layer can show a modal + log out.
 */
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 403 &&
      detail &&
      typeof detail === "object" &&
      detail.code === "LODGE_FROZEN"
    ) {
      try {
        window.dispatchEvent(new CustomEvent("lodge-frozen", { detail }));
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(err);
  },
);

/** Last-resort translations for legacy backend errors that still ship as
 *  Romanian-only strings. New code paths should return structured
 *  `{message_ro, message_en}` payloads instead.
 */
const LEGACY_RO_TO_EN = {
  "Membru inexistent": "Member not found",
  "Lojă inexistentă": "Lodge not found",
  "Utilizator inexistent": "User not found",
  "Username sau parolă incorectă": "Invalid username or password",
  "Username și parolă sunt obligatorii": "Username and password are required",
  "Username deja folosit": "Username already in use",
  "Utilizatorul există deja": "User already exists",
  "Membrul aparține deja lojei tale": "This mason already belongs to your lodge",
  "Cerere de transfer inexistentă sau deja procesată": "Transfer request not found or already processed",
  "Doar loja origine poate aproba transferul": "Only the origin lodge can approve the transfer",
  "Doar loja origine poate respinge transferul": "Only the origin lodge can reject the transfer",
  "Acces interzis": "Access denied",
  "Neautentificat": "Not authenticated",
  "Token invalid": "Invalid token",
  "Cale invalidă": "Invalid path",
  "Pașaport invalid.": "Invalid passport.",
  "Parola curentă incorectă": "Current password is incorrect",
  "Parola nouă trebuie să aibă minim 4 caractere": "The new password must be at least 4 characters",
  "Adminul nu are o lojă atribuită": "Admin has no lodge assigned",
  "Super-admin nu creează membri direct": "Super-admin cannot create members directly",
  "A apărut o eroare": "Something went wrong",
};

function translateLegacy(text) {
  if (typeof text !== "string") return text;
  const lang =
    typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem("i18nextLng") || "ro"
      : "ro";
  if (!lang.startsWith("en")) return text;
  return LEGACY_RO_TO_EN[text] || text;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return translateLegacy(err?.message) || translateLegacy("A apărut o eroare");
  if (typeof detail === "string") return translateLegacy(detail);
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? translateLegacy(e.msg) : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return translateLegacy(detail.msg);
  // Structured detail like LODGE_FROZEN ({code, message_ro, message_en})
  if (detail && typeof detail === "object") {
    const lang =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("i18nextLng") || "ro"
        : "ro";
    if (lang.startsWith("en") && typeof detail.message_en === "string") return detail.message_en;
    if (typeof detail.message_ro === "string") return detail.message_ro;
    if (typeof detail.message_en === "string") return detail.message_en;
  }
  return translateLegacy(String(detail));
}

/** Return the user's currently-selected UI language as a 2-letter ISO code,
 *  restricted to the supported set. Used by PDF/CSV download paths and the
 *  Accept-Language interceptor so the backend always serves the same locale
 *  the user is reading on screen. */
export const SUPPORTED_LANG_CODES = ["ro", "en", "fr", "de", "it", "es", "pt", "pt-BR", "sr"];

/** Normalise any locale tag to one of SUPPORTED_LANG_CODES (regional pt-BR kept). */
export function normalizeLangCode(raw) {
  const v = String(raw || "").replace("_", "-");
  const exact = SUPPORTED_LANG_CODES.find((c) => c.toLowerCase() === v.toLowerCase());
  if (exact) return exact;
  const base = v.split("-")[0].toLowerCase();
  return SUPPORTED_LANG_CODES.includes(base) ? base : "en";
}

export function currentLangCode() {
  try {
    const raw =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("sc_lang") ||
          window.localStorage.getItem("i18nextLng") ||
          "ro"
        : "ro";
    return normalizeLangCode(raw);
  } catch {
    return "ro";
  }
}

export function authQuery() {
  // Downloads / `<img>` tags inherit the httpOnly `access_token` cookie set at
  // login (SameSite=Lax, Secure). Passing the bearer token in a query string
  // would leak it into access logs / referrer headers, so we rely solely on
  // the cookie. We still append localisation + child-lodge context because
  // those are not security-sensitive.
  const child = localStorage.getItem("sc_active_child_lodge");
  const parts = [`lang=${currentLangCode()}`];
  if (child) parts.push(`active_child_lodge=${encodeURIComponent(child)}`);
  return `?${parts.join("&")}`;
}

/**
 * Trigger a file download for a URL that requires authentication.
 *
 * Uses an authenticated fetch() + blob so downloads work in any deployment
 * topology (including cross-origin where 3rd-party cookies may be blocked).
 * Falls back to a plain anchor click if fetch fails (keeps backwards
 * compat with public/no-auth endpoints).
 */
export async function downloadUrl(url, filename) {
  try {
    const token = localStorage.getItem("sc_token");
    const lang = currentLangCode();
    const child = localStorage.getItem("sc_active_child_lodge");
    const headers = { "Accept-Language": lang };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (child) headers["X-Active-Child-Lodge"] = child;
    const resp = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers,
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    // Honour the filename sent by the backend if the caller didn't pass one.
    let fname = filename;
    if (!fname) {
      const cd = resp.headers.get("Content-Disposition") || "";
      const starMatch = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = cd.match(/filename="?([^";]+)"?/i);
      if (starMatch) {
        try { fname = decodeURIComponent(starMatch[1]); } catch { fname = starMatch[1]; }
      } else if (plainMatch) {
        fname = plainMatch[1];
      }
    }
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    if (fname) a.download = fname;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (err) {
    // Fallback: plain anchor — works for any public endpoint or same-origin
    // deployment where cookies flow naturally.
    const a = document.createElement("a");
    a.href = url;
    if (filename) a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
  }
}
